import type { SvgIconProps } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import FlagIcon from '@mui/icons-material/Flag';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import InsightsIcon from '@mui/icons-material/Insights';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CampaignIcon from '@mui/icons-material/Campaign';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import CodeIcon from '@mui/icons-material/Code';
import BarChartIcon from '@mui/icons-material/BarChart';
import GroupsIcon from '@mui/icons-material/Groups';
import GavelIcon from '@mui/icons-material/Gavel';
import SchoolIcon from '@mui/icons-material/School';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import BuildIcon from '@mui/icons-material/Build';
import SecurityIcon from '@mui/icons-material/Security';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MapIcon from '@mui/icons-material/Map';
import ArticleIcon from '@mui/icons-material/Article';
import StorageIcon from '@mui/icons-material/Storage';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

/**
 * Curated, statically-imported subset of `@mui/icons-material` — the full
 * package (`import * as`) added ~4MB (~680KB gzip) to the bundle, so avatar
 * icons are looked up from this fixed map instead. `avatarIcon` on the model
 * remains a free-form string; a name outside this map just falls back to the
 * generic icon below rather than failing.
 */
export const CURATED_ICON_NAMES = [
  'Flag', 'TravelExplore', 'Insights', 'Assignment', 'AccountTree', 'Campaign',
  'Psychology', 'Lightbulb', 'Architecture', 'Code', 'BarChart', 'Groups',
  'Gavel', 'School', 'RocketLaunch', 'Build', 'Security', 'DesignServices',
  'BusinessCenter', 'SupportAgent', 'TrendingUp', 'Map', 'Article', 'Storage',
  'Checklist', 'AutoAwesome', 'SmartToy',
] as const;

const ICONS: Record<string, typeof SmartToyIcon> = {
  Flag: FlagIcon,
  TravelExplore: TravelExploreIcon,
  Insights: InsightsIcon,
  Assignment: AssignmentIcon,
  AccountTree: AccountTreeIcon,
  Campaign: CampaignIcon,
  Psychology: PsychologyIcon,
  Lightbulb: LightbulbIcon,
  Architecture: ArchitectureIcon,
  Code: CodeIcon,
  BarChart: BarChartIcon,
  Groups: GroupsIcon,
  Gavel: GavelIcon,
  School: SchoolIcon,
  RocketLaunch: RocketLaunchIcon,
  Build: BuildIcon,
  Security: SecurityIcon,
  DesignServices: DesignServicesIcon,
  BusinessCenter: BusinessCenterIcon,
  SupportAgent: SupportAgentIcon,
  TrendingUp: TrendingUpIcon,
  Map: MapIcon,
  Article: ArticleIcon,
  Storage: StorageIcon,
  Checklist: ChecklistIcon,
  AutoAwesome: AutoAwesomeIcon,
  SmartToy: SmartToyIcon,
};

interface DynamicMuiIconProps extends SvgIconProps {
  /** MUI icon component name, e.g. "Flag", "TravelExplore". Falls back to a generic icon when unknown/empty. */
  iconName?: string | null;
}

/** Renders one of `CURATED_ICON_NAMES` looked up by name, falling back to a generic icon. */
export function DynamicMuiIcon({ iconName, ...props }: DynamicMuiIconProps) {
  const Icon = (iconName && ICONS[iconName]) || SmartToyIcon;
  return <Icon {...props} />;
}
